// UI texts. Game texts the app already has (pack names, "Who won?", the
// rules, ...) come from the app's ARB files via app-strings.gen.json, in all
// 26 languages. The texts below exist only on the web; languages without
// them fall back to English.

import appStrings from './app-strings.gen.json';

const app = appStrings as Record<string, Record<string, string>>;

const en = {
  tagline: 'The $20 auction draft from TikTok, right in your browser. Built for streams.',
  playFree: 'Free to play. No account, no download.',
  howItWorks: 'How it works',
  players: 'Players',
  player: 'Player {n}',
  packs: 'Packs',
  all: 'All',
  none: 'None',
  themes: '{count} themes',
  show18: 'Show 18+ packs',
  adultWarning:
    'The 18+ packs contain sexual humour. Only for adult audiences: check your streaming platform\'s rules before you show them.',
  adultConfirm: 'I am 18 or older',
  readAloud: 'Read the verdict aloud',
  sounds: 'Sound effects',
  showLink: 'Show the game link on screen',
  twitch: 'Twitch chat',
  twitchDesc: 'Read-only, no login. Chat can pick the theme and vote for the winner.',
  channel: 'Channel name',
  connect: 'Connect',
  disconnect: 'Disconnect',
  connecting: 'Connecting…',
  connected: 'Connected to #{channel}',
  connectFailed: 'Could not connect. Check the channel name.',
  chatPicksTheme: 'Chat picks the theme',
  chatJudges: 'Chat votes for the winner',
  start: 'Let\'s go',
  pickPack: 'Pick at least one pack.',
  loading: 'Loading…',
  themeVote: 'Chat, pick the theme!',
  themeVoteHow: 'Type 1, 2 or 3 in chat',
  votes: '{count} votes',
  closeVote: 'Close the vote',
  pickThis: 'Pick',
  chatVote: 'Chat, who won?',
  chatVoteHow: 'Type 1 for {p1} or 2 for {p2}',
  askChat: 'Ask chat',
  wins: '{name} wins!',
  tie: 'It\'s a tie!',
  chatSays: 'Chat says',
  aiSays: 'The AI judge says',
  undo: 'Undo',
  keys: 'Keys: ← → price · 1 / 2 sell · Z undo',
  sold: 'Sold to {name} for ${price}!',
  getApp: 'Get the app',
  playAt: 'Play free: {url}',
  menu: 'Menu',
  quitRound: 'Quit this round?',
  quit: 'Quit round',
  keepPlaying: 'Keep playing',
  cancel: 'Cancel',
  fullscreen: 'Fullscreen',
  imageSaved: 'Image downloaded',
  biggestBuy: 'Biggest buy: {item} for ${price}',
  left: '${amount} left',
  verdictLimit: 'The AI judge needs a short break. Try again in a minute.',
  verdictQuota: 'That was today\'s last free AI verdict.',
  language: 'Language',
  imprint: 'Imprint',
  privacy: 'Privacy',
  terms: 'Terms',
  pro: 'Pro',
  proTitle: 'Social Gamebox Pro',
  proBody: 'Every pack and unlimited AI verdicts, on this browser.',
  proBuy: 'Unlock for {price}',
  proBuyNoPrice: 'Unlock Pro',
  proActive: 'Pro is active',
  proCode: 'Your purchase code: {code}. Keep it to unlock Pro on another device.',
  restore: 'Restore a purchase',
  restoreCode: 'Purchase code',
  restoreOk: 'Pro restored.',
  restoreFail: 'That code did not work.',
  checkoutFail: 'Checkout is not available right now.',
  locked: 'Locked',
  close: 'Close',
  streamTip: 'Stream tip: add this tab as a window or browser source (1920×1080).',
  againSameTheme: 'Replay theme',
  backToSetup: 'Setup',
  rotate: 'Turn your phone sideways to play, or get the app.',
  continueAnyway: 'Continue anyway',
};

export type WebKey = keyof typeof en;

const de: Partial<Record<WebKey, string>> = {
  tagline: 'Die $20-Auktion von TikTok, direkt im Browser. Gemacht für Streams.',
  playFree: 'Kostenlos spielen. Kein Konto, kein Download.',
  howItWorks: 'So geht\'s',
  players: 'Spieler',
  player: 'Spieler {n}',
  packs: 'Pakete',
  all: 'Alle',
  none: 'Keine',
  themes: '{count} Themen',
  show18: '18+-Pakete zeigen',
  adultWarning:
    'Die 18+-Pakete enthalten sexuellen Humor. Nur für ein erwachsenes Publikum: Prüf die Regeln deiner Streaming-Plattform, bevor du sie zeigst.',
  adultConfirm: 'Ich bin mindestens 18',
  readAloud: 'Urteil vorlesen',
  sounds: 'Soundeffekte',
  showLink: 'Spiel-Link im Bild zeigen',
  twitch: 'Twitch-Chat',
  twitchDesc: 'Nur lesen, kein Login. Der Chat kann das Thema wählen und den Sieger küren.',
  channel: 'Kanalname',
  connect: 'Verbinden',
  disconnect: 'Trennen',
  connecting: 'Verbinde…',
  connected: 'Verbunden mit #{channel}',
  connectFailed: 'Verbindung fehlgeschlagen. Stimmt der Kanalname?',
  chatPicksTheme: 'Chat wählt das Thema',
  chatJudges: 'Chat stimmt über den Sieger ab',
  start: 'Los geht\'s',
  pickPack: 'Wähl mindestens ein Paket.',
  loading: 'Lädt…',
  themeVote: 'Chat, wählt das Thema!',
  themeVoteHow: 'Schreibt 1, 2 oder 3 in den Chat',
  votes: '{count} Stimmen',
  closeVote: 'Abstimmung beenden',
  pickThis: 'Nehmen',
  chatVote: 'Chat, wer hat gewonnen?',
  chatVoteHow: 'Schreibt 1 für {p1} oder 2 für {p2}',
  askChat: 'Chat fragen',
  wins: '{name} gewinnt!',
  tie: 'Unentschieden!',
  chatSays: 'Der Chat sagt',
  aiSays: 'Die KI-Jury sagt',
  undo: 'Rückgängig',
  keys: 'Tasten: ← → Preis · 1 / 2 verkaufen · Z rückgängig',
  sold: 'Verkauft an {name} für ${price}!',
  getApp: 'App holen',
  playAt: 'Gratis spielen: {url}',
  menu: 'Menü',
  quitRound: 'Runde abbrechen?',
  quit: 'Runde beenden',
  keepPlaying: 'Weiterspielen',
  cancel: 'Abbrechen',
  fullscreen: 'Vollbild',
  imageSaved: 'Bild heruntergeladen',
  biggestBuy: 'Teuerster Kauf: {item} für ${price}',
  left: '${amount} übrig',
  verdictLimit: 'Die KI-Jury braucht kurz Pause. Versuch es in einer Minute nochmal.',
  verdictQuota: 'Das war das letzte kostenlose KI-Urteil für heute.',
  language: 'Sprache',
  imprint: 'Impressum',
  privacy: 'Datenschutz',
  terms: 'AGB',
  proBody: 'Alle Pakete und unbegrenzt KI-Urteile, in diesem Browser.',
  proBuy: 'Freischalten für {price}',
  proBuyNoPrice: 'Pro freischalten',
  proActive: 'Pro ist aktiv',
  proCode: 'Dein Kauf-Code: {code}. Heb ihn auf, um Pro auf einem anderen Gerät freizuschalten.',
  restore: 'Kauf wiederherstellen',
  restoreCode: 'Kauf-Code',
  restoreOk: 'Pro wiederhergestellt.',
  restoreFail: 'Der Code hat nicht funktioniert.',
  checkoutFail: 'Der Kauf ist gerade nicht möglich.',
  locked: 'Gesperrt',
  close: 'Schließen',
  streamTip: 'Stream-Tipp: Tab als Fenster- oder Browserquelle einbinden (1920×1080).',
  againSameTheme: 'Thema nochmal',
  backToSetup: 'Einstellungen',
  rotate: 'Dreh dein Handy quer zum Spielen oder hol dir die App.',
  continueAnyway: 'Trotzdem weiter',
};

const fr: Partial<Record<WebKey, string>> = {
  tagline: 'Le draft aux enchères à 20 $ de TikTok, dans ton navigateur. Pensé pour les streams.',
  playFree: 'Gratuit. Sans compte, sans téléchargement.',
  howItWorks: 'Comment ça marche',
  players: 'Joueurs',
  player: 'Joueur {n}',
  packs: 'Packs',
  all: 'Tous',
  none: 'Aucun',
  themes: '{count} thèmes',
  show18: 'Afficher les packs 18+',
  adultWarning:
    'Les packs 18+ contiennent de l\'humour sexuel. Réservés à un public adulte : vérifie les règles de ta plateforme avant de les montrer.',
  adultConfirm: 'J\'ai 18 ans ou plus',
  readAloud: 'Lire le verdict à voix haute',
  sounds: 'Effets sonores',
  showLink: 'Afficher le lien du jeu à l\'écran',
  twitch: 'Chat Twitch',
  twitchDesc: 'Lecture seule, sans connexion. Le chat peut choisir le thème et voter pour le gagnant.',
  channel: 'Nom de la chaîne',
  connect: 'Connecter',
  disconnect: 'Déconnecter',
  connecting: 'Connexion…',
  connected: 'Connecté à #{channel}',
  connectFailed: 'Connexion impossible. Vérifie le nom de la chaîne.',
  chatPicksTheme: 'Le chat choisit le thème',
  chatJudges: 'Le chat vote pour le gagnant',
  start: 'C\'est parti',
  pickPack: 'Choisis au moins un pack.',
  loading: 'Chargement…',
  themeVote: 'Chat, choisissez le thème !',
  themeVoteHow: 'Tapez 1, 2 ou 3 dans le chat',
  votes: '{count} votes',
  closeVote: 'Clore le vote',
  pickThis: 'Choisir',
  chatVote: 'Chat, qui a gagné ?',
  chatVoteHow: 'Tapez 1 pour {p1} ou 2 pour {p2}',
  askChat: 'Demander au chat',
  wins: '{name} gagne !',
  tie: 'Égalité !',
  chatSays: 'Le chat dit',
  aiSays: 'Le juge IA dit',
  undo: 'Annuler',
  keys: 'Touches : ← → prix · 1 / 2 vendre · Z annuler',
  sold: 'Vendu à {name} pour {price} $ !',
  getApp: 'Télécharger l\'app',
  playAt: 'Joue gratuitement : {url}',
  menu: 'Menu',
  quitRound: 'Quitter la manche ?',
  quit: 'Quitter',
  keepPlaying: 'Continuer',
  cancel: 'Annuler',
  fullscreen: 'Plein écran',
  imageSaved: 'Image téléchargée',
  biggestBuy: 'Plus gros achat : {item} pour {price} $',
  left: '{amount} $ restants',
  verdictLimit: 'Le juge IA fait une pause. Réessaie dans une minute.',
  verdictQuota: 'C\'était le dernier verdict IA gratuit du jour.',
  language: 'Langue',
  imprint: 'Mentions légales',
  privacy: 'Confidentialité',
  terms: 'CGU',
  close: 'Fermer',
  streamTip: 'Astuce stream : ajoute cet onglet comme source fenêtre ou navigateur (1920×1080).',
  againSameTheme: 'Rejouer le thème',
  backToSetup: 'Réglages',
  rotate: 'Tourne ton téléphone pour jouer, ou télécharge l\'app.',
  continueAnyway: 'Continuer quand même',
};

const es: Partial<Record<WebKey, string>> = {
  tagline: 'El draft de subasta de 20 $ de TikTok, en tu navegador. Hecho para streams.',
  playFree: 'Gratis. Sin cuenta, sin descargas.',
  howItWorks: 'Cómo se juega',
  players: 'Jugadores',
  player: 'Jugador {n}',
  packs: 'Packs',
  all: 'Todos',
  none: 'Ninguno',
  themes: '{count} temas',
  show18: 'Mostrar packs +18',
  adultWarning:
    'Los packs +18 tienen humor sexual. Solo para público adulto: revisa las normas de tu plataforma antes de mostrarlos.',
  adultConfirm: 'Tengo 18 años o más',
  readAloud: 'Leer el veredicto en voz alta',
  sounds: 'Efectos de sonido',
  showLink: 'Mostrar el enlace del juego en pantalla',
  twitch: 'Chat de Twitch',
  twitchDesc: 'Solo lectura, sin iniciar sesión. El chat puede elegir el tema y votar al ganador.',
  channel: 'Nombre del canal',
  connect: 'Conectar',
  disconnect: 'Desconectar',
  connecting: 'Conectando…',
  connected: 'Conectado a #{channel}',
  connectFailed: 'No se pudo conectar. Revisa el nombre del canal.',
  chatPicksTheme: 'El chat elige el tema',
  chatJudges: 'El chat vota al ganador',
  start: '¡Vamos!',
  pickPack: 'Elige al menos un pack.',
  loading: 'Cargando…',
  themeVote: '¡Chat, elegid el tema!',
  themeVoteHow: 'Escribid 1, 2 o 3 en el chat',
  votes: '{count} votos',
  closeVote: 'Cerrar votación',
  pickThis: 'Elegir',
  chatVote: 'Chat, ¿quién ganó?',
  chatVoteHow: 'Escribid 1 para {p1} o 2 para {p2}',
  askChat: 'Preguntar al chat',
  wins: '¡Gana {name}!',
  tie: '¡Empate!',
  chatSays: 'El chat dice',
  aiSays: 'El juez IA dice',
  undo: 'Deshacer',
  keys: 'Teclas: ← → precio · 1 / 2 vender · Z deshacer',
  sold: '¡Vendido a {name} por {price} $!',
  getApp: 'Descarga la app',
  playAt: 'Juega gratis: {url}',
  menu: 'Menú',
  quitRound: '¿Salir de la ronda?',
  quit: 'Salir',
  keepPlaying: 'Seguir',
  cancel: 'Cancelar',
  fullscreen: 'Pantalla completa',
  imageSaved: 'Imagen descargada',
  biggestBuy: 'Compra más cara: {item} por {price} $',
  left: 'Quedan {amount} $',
  verdictLimit: 'El juez IA necesita un descanso. Inténtalo en un minuto.',
  verdictQuota: 'Ese fue el último veredicto IA gratis de hoy.',
  language: 'Idioma',
  imprint: 'Aviso legal',
  privacy: 'Privacidad',
  terms: 'Términos',
  close: 'Cerrar',
  streamTip: 'Consejo: añade esta pestaña como fuente de ventana o navegador (1920×1080).',
  againSameTheme: 'Repetir tema',
  backToSetup: 'Ajustes',
  rotate: 'Gira el móvil en horizontal para jugar, o descarga la app.',
  continueAnyway: 'Seguir igualmente',
};

const it: Partial<Record<WebKey, string>> = {
  tagline: 'Il draft all\'asta da 20 $ di TikTok, nel tuo browser. Fatto per le live.',
  playFree: 'Gratis. Niente account, niente download.',
  howItWorks: 'Come si gioca',
  players: 'Giocatori',
  player: 'Giocatore {n}',
  packs: 'Pacchetti',
  all: 'Tutti',
  none: 'Nessuno',
  themes: '{count} temi',
  show18: 'Mostra pacchetti 18+',
  adultWarning:
    'I pacchetti 18+ contengono umorismo sessuale. Solo per un pubblico adulto: controlla le regole della tua piattaforma prima di mostrarli.',
  adultConfirm: 'Ho almeno 18 anni',
  readAloud: 'Leggi il verdetto ad alta voce',
  sounds: 'Effetti sonori',
  showLink: 'Mostra il link del gioco sullo schermo',
  twitch: 'Chat di Twitch',
  twitchDesc: 'Sola lettura, senza login. La chat può scegliere il tema e votare il vincitore.',
  channel: 'Nome del canale',
  connect: 'Collega',
  disconnect: 'Scollega',
  connecting: 'Connessione…',
  connected: 'Collegato a #{channel}',
  connectFailed: 'Connessione non riuscita. Controlla il nome del canale.',
  chatPicksTheme: 'La chat sceglie il tema',
  chatJudges: 'La chat vota il vincitore',
  start: 'Si parte',
  pickPack: 'Scegli almeno un pacchetto.',
  loading: 'Caricamento…',
  themeVote: 'Chat, scegliete il tema!',
  themeVoteHow: 'Scrivete 1, 2 o 3 in chat',
  votes: '{count} voti',
  closeVote: 'Chiudi la votazione',
  pickThis: 'Scegli',
  chatVote: 'Chat, chi ha vinto?',
  chatVoteHow: 'Scrivete 1 per {p1} o 2 per {p2}',
  askChat: 'Chiedi alla chat',
  wins: 'Vince {name}!',
  tie: 'Pareggio!',
  chatSays: 'La chat dice',
  aiSays: 'Il giudice IA dice',
  undo: 'Annulla',
  keys: 'Tasti: ← → prezzo · 1 / 2 vendi · Z annulla',
  sold: 'Venduto a {name} per {price} $!',
  getApp: 'Scarica l\'app',
  playAt: 'Gioca gratis: {url}',
  menu: 'Menu',
  quitRound: 'Uscire dal round?',
  quit: 'Esci',
  keepPlaying: 'Continua',
  cancel: 'Annulla',
  fullscreen: 'Schermo intero',
  imageSaved: 'Immagine scaricata',
  biggestBuy: 'Acquisto più caro: {item} per {price} $',
  left: '{amount} $ rimasti',
  verdictLimit: 'Il giudice IA è in pausa. Riprova tra un minuto.',
  verdictQuota: 'Era l\'ultimo verdetto IA gratuito di oggi.',
  language: 'Lingua',
  imprint: 'Note legali',
  privacy: 'Privacy',
  terms: 'Termini',
  close: 'Chiudi',
  streamTip: 'Consiglio: aggiungi questa scheda come sorgente finestra o browser (1920×1080).',
  againSameTheme: 'Rigioca il tema',
  backToSetup: 'Impostazioni',
  rotate: 'Gira il telefono in orizzontale per giocare, o scarica l\'app.',
  continueAnyway: 'Continua comunque',
};

const ptBR: Partial<Record<WebKey, string>> = {
  tagline: 'O draft de leilão de US$ 20 do TikTok, no seu navegador. Feito para lives.',
  playFree: 'Grátis. Sem conta, sem download.',
  howItWorks: 'Como jogar',
  players: 'Jogadores',
  player: 'Jogador {n}',
  packs: 'Pacotes',
  all: 'Todos',
  none: 'Nenhum',
  themes: '{count} temas',
  show18: 'Mostrar pacotes +18',
  adultWarning:
    'Os pacotes +18 têm humor sexual. Só para público adulto: confira as regras da sua plataforma antes de mostrar.',
  adultConfirm: 'Tenho 18 anos ou mais',
  readAloud: 'Ler o veredito em voz alta',
  sounds: 'Efeitos sonoros',
  showLink: 'Mostrar o link do jogo na tela',
  twitch: 'Chat da Twitch',
  twitchDesc: 'Só leitura, sem login. O chat pode escolher o tema e votar no vencedor.',
  channel: 'Nome do canal',
  connect: 'Conectar',
  disconnect: 'Desconectar',
  connecting: 'Conectando…',
  connected: 'Conectado a #{channel}',
  connectFailed: 'Não deu para conectar. Confira o nome do canal.',
  chatPicksTheme: 'O chat escolhe o tema',
  chatJudges: 'O chat vota no vencedor',
  start: 'Bora',
  pickPack: 'Escolha pelo menos um pacote.',
  loading: 'Carregando…',
  themeVote: 'Chat, escolham o tema!',
  themeVoteHow: 'Digitem 1, 2 ou 3 no chat',
  votes: '{count} votos',
  closeVote: 'Encerrar votação',
  pickThis: 'Escolher',
  chatVote: 'Chat, quem ganhou?',
  chatVoteHow: 'Digitem 1 para {p1} ou 2 para {p2}',
  askChat: 'Perguntar ao chat',
  wins: '{name} venceu!',
  tie: 'Empate!',
  chatSays: 'O chat diz',
  aiSays: 'O juiz de IA diz',
  undo: 'Desfazer',
  keys: 'Teclas: ← → preço · 1 / 2 vender · Z desfazer',
  sold: 'Vendido para {name} por US$ {price}!',
  getApp: 'Baixe o app',
  playAt: 'Jogue grátis: {url}',
  menu: 'Menu',
  quitRound: 'Sair da rodada?',
  quit: 'Sair',
  keepPlaying: 'Continuar',
  cancel: 'Cancelar',
  fullscreen: 'Tela cheia',
  imageSaved: 'Imagem baixada',
  biggestBuy: 'Compra mais cara: {item} por US$ {price}',
  left: 'Restam US$ {amount}',
  verdictLimit: 'O juiz de IA precisa de uma pausa. Tente de novo em um minuto.',
  verdictQuota: 'Esse foi o último veredito de IA grátis de hoje.',
  language: 'Idioma',
  imprint: 'Aviso legal',
  privacy: 'Privacidade',
  terms: 'Termos',
  close: 'Fechar',
  streamTip: 'Dica: adicione esta aba como fonte de janela ou navegador (1920×1080).',
  againSameTheme: 'Repetir tema',
  backToSetup: 'Ajustes',
  rotate: 'Vire o celular na horizontal para jogar, ou baixe o app.',
  continueAnyway: 'Continuar mesmo assim',
};

const WEB: Record<string, Partial<Record<WebKey, string>>> = { en, de, fr, es, it, 'pt-BR': ptBR };

export type AppKey = keyof (typeof app)['en'];

export function fill(s: string, params?: Record<string, string | number>): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m));
}

export function makeT(lang: string) {
  const web = WEB[lang] ?? {};
  const appL = app[lang] ?? {};
  const appEn = app.en ?? {};
  const t = (key: WebKey, params?: Record<string, string | number>) => fill(web[key] ?? en[key], params);
  const a = (key: string, params?: Record<string, string | number>) => fill(appL[key] ?? appEn[key] ?? key, params);
  return { t, a, lang };
}

export type T = ReturnType<typeof makeT>;

export const LANGUAGE_LABELS: Record<string, string> = {
  ar: 'العربية',
  bn: 'বাংলা',
  cs: 'Čeština',
  de: 'Deutsch',
  el: 'Ελληνικά',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  hi: 'हिन्दी',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  nb: 'Norsk',
  nl: 'Nederlands',
  pl: 'Polski',
  'pt-BR': 'Português (Brasil)',
  ro: 'Română',
  ru: 'Русский',
  sv: 'Svenska',
  tr: 'Türkçe',
  uk: 'Українська',
  vi: 'Tiếng Việt',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
};

export const RTL = new Set(['ar']);

/// The content language closest to the browser's.
export function detectLanguage(available: string[]): string {
  const prefs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const p of prefs) {
    const tag = p.toLowerCase();
    const exact = available.find((l) => l.toLowerCase() === tag);
    if (exact) return exact;
    if (tag.startsWith('pt')) return available.includes('pt-BR') ? 'pt-BR' : 'en';
    if (tag.startsWith('zh')) {
      const tw = /tw|hk|hant/.test(tag);
      return tw ? 'zh-TW' : 'zh-CN';
    }
    if (tag.startsWith('no') || tag.startsWith('nn')) return 'nb';
    const base = available.find((l) => l === tag.split('-')[0]);
    if (base) return base;
  }
  return 'en';
}
